/**
 * Video Frame URL Cache
 *
 * Caches frame URLs by video ID and frame number to avoid redundant API calls
 * when video elements are split or duplicated. This significantly improves
 * performance during video splitting operations.
 */

// Cache structure: Map<frameKey, frameUrl>
// frameKey format: "${storyId}-${videoId}-${frameNumber}"
const frameUrlCache = new Map<string, string>();

// Cache listeners for cross-component updates
const cacheUpdateListeners = new Set<() => void>();

const revokeIfLocal = (url: string | undefined): void => {
  if (
    url?.startsWith('blob:') &&
    typeof URL !== 'undefined' &&
    typeof URL.revokeObjectURL === 'function'
  ) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Generate a unique cache key for a frame
 */
const getFrameKey = (
  storyId: string,
  videoId: string,
  frameNumber: number,
): string => {
  return `${storyId}-${videoId}-${frameNumber}`;
};

/**
 * Get cached frame URL if available
 */
export const getCachedFrameUrl = (
  storyId: string,
  videoId: string,
  frameNumber: number,
): string | null => {
  const key = getFrameKey(storyId, videoId, frameNumber);
  return frameUrlCache.get(key) || null;
};

/**
 * Cache a frame URL
 */
export const setCachedFrameUrl = (
  storyId: string,
  videoId: string,
  frameNumber: number,
  url: string,
): void => {
  const key = getFrameKey(storyId, videoId, frameNumber);
  const previous = frameUrlCache.get(key);
  if (previous && previous !== url) revokeIfLocal(previous);
  frameUrlCache.set(key, url);

  // Notify listeners about cache update
  notifyCacheListeners();
};

/**
 * Check if a frame URL is cached
 */
export const isFrameCached = (
  storyId: string,
  videoId: string,
  frameNumber: number,
): boolean => {
  const key = getFrameKey(storyId, videoId, frameNumber);
  return frameUrlCache.has(key);
};

/**
 * Get multiple cached frame URLs
 * Returns a map of frameNumber -> url for frames that are cached
 */
export const getCachedFrameUrls = (
  storyId: string,
  videoId: string,
  frameNumbers: number[],
): Map<number, string> => {
  const cachedFrames = new Map<number, string>();

  frameNumbers.forEach(frameNumber => {
    const url = getCachedFrameUrl(storyId, videoId, frameNumber);
    if (url) {
      cachedFrames.set(frameNumber, url);
    }
  });

  return cachedFrames;
};

/**
 * Cache multiple frame URLs at once
 */
export const setCachedFrameUrls = (
  storyId: string,
  videoId: string,
  frames: Array<{ frameNumber: number; url: string }>,
): void => {
  frames.forEach(({ frameNumber, url }) => {
    const key = getFrameKey(storyId, videoId, frameNumber);
    const previous = frameUrlCache.get(key);
    if (previous && previous !== url) revokeIfLocal(previous);
    frameUrlCache.set(key, url);
  });

  // Notify listeners about cache update
  notifyCacheListeners();
};

/**
 * Filter out frame numbers that are already cached
 * Returns only the frame numbers that need to be loaded
 */
export const getUncachedFrameNumbers = (
  storyId: string,
  videoId: string,
  frameNumbers: number[],
): number[] => {
  return frameNumbers.filter(
    frameNumber => !isFrameCached(storyId, videoId, frameNumber),
  );
};

/**
 * Clear cache for a specific video
 */
export const clearVideoFrameCache = (
  storyId: string,
  videoId: string,
): void => {
  const prefix = `${storyId}-${videoId}-`;

  // Remove all entries for this video
  for (const key of frameUrlCache.keys()) {
    if (key.startsWith(prefix)) {
      revokeIfLocal(frameUrlCache.get(key));
      frameUrlCache.delete(key);
    }
  }

  // Notify listeners about cache update
  notifyCacheListeners();
};

/**
 * Clear entire frame cache
 */
export const clearAllFrameCache = (): void => {
  frameUrlCache.forEach(revokeIfLocal);
  frameUrlCache.clear();
  notifyCacheListeners();
};

/**
 * Add a listener for cache updates
 */
export const addFrameCacheUpdateListener = (
  listener: () => void,
): (() => void) => {
  cacheUpdateListeners.add(listener);

  // Return cleanup function
  return () => {
    cacheUpdateListeners.delete(listener);
  };
};

/**
 * Notify all cache listeners
 */
const notifyCacheListeners = (): void => {
  cacheUpdateListeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      console.warn('Error in frame cache listener:', error);
    }
  });
};

/**
 * Find cached frames that are close to the requested frame numbers
 * This helps reuse frames when video segments have slightly different frame distributions
 */
export const findNearestCachedFrames = (
  storyId: string,
  videoId: string,
  requestedFrameNumbers: number[],
  tolerance: number = 2,
): Map<number, { frameNumber: number; url: string }> => {
  const nearestFrames = new Map<number, { frameNumber: number; url: string }>();
  const prefix = `${storyId}-${videoId}-`;

  // Get all cached frame numbers for this video
  const cachedFrameNumbers: number[] = [];
  for (const key of frameUrlCache.keys()) {
    if (key.startsWith(prefix)) {
      const frameNumber = parseInt(key.substring(prefix.length));
      if (!isNaN(frameNumber)) {
        cachedFrameNumbers.push(frameNumber);
      }
    }
  }

  // For each requested frame, find the nearest cached frame within tolerance
  requestedFrameNumbers.forEach(requestedFrame => {
    let nearestFrame: number | null = null;
    let minDistance = Infinity;

    cachedFrameNumbers.forEach(cachedFrame => {
      const distance = Math.abs(cachedFrame - requestedFrame);
      if (distance <= tolerance && distance < minDistance) {
        minDistance = distance;
        nearestFrame = cachedFrame;
      }
    });

    if (nearestFrame !== null) {
      const url = getCachedFrameUrl(storyId, videoId, nearestFrame);
      if (url) {
        nearestFrames.set(requestedFrame, { frameNumber: nearestFrame, url });
      }
    }
  });

  return nearestFrames;
};

/**
 * Video-level frame layout cache
 * Stores the "master" frame layout for each video to ensure consistency across splits
 */
const videoFrameLayoutCache = new Map<
  string,
  Array<{ frameNumber: number; time: number }>
>();

/**
 * Get or create a master frame layout for a video
 * This ensures all segments of the same video use consistent frame distribution
 */
export const getVideoFrameLayout = (
  storyId: string,
  videoId: string,
  videoDuration: number,
  forceRecalculate: boolean = false,
): Array<{ frameNumber: number; time: number }> => {
  const videoKey = `${storyId}-${videoId}`;

  if (!forceRecalculate && videoFrameLayoutCache.has(videoKey)) {
    return videoFrameLayoutCache.get(videoKey)!;
  }

  // Create master frame layout for the entire video
  const FRAME_INTERVAL = 0.5; // 500ms intervals
  const frameCount = Math.max(1, Math.ceil(videoDuration / FRAME_INTERVAL));

  const layout = Array.from({ length: frameCount }, (_, i) => {
    const time = i * FRAME_INTERVAL;
    // Frame number should correspond to the actual second in the video
    // Since frames are saved one per second, frame number = seconds (rounded)
    const frameNumber = Math.max(1, Math.round(time));
    return { frameNumber, time };
  });

  videoFrameLayoutCache.set(videoKey, layout);
  return layout;
};

/**
 * Get frames for a specific segment of a video
 * Uses the master frame layout and filters to the segment's time range
 * Ensures every segment gets at least one frame, even very short ones
 */
export const getSegmentFrames = (
  storyId: string,
  videoId: string,
  videoDuration: number,
  segmentStart: number,
  segmentDuration: number,
  maxFrames: number = 50,
): Array<{ frameNumber: number; time: number; index: number }> => {
  const masterLayout = getVideoFrameLayout(storyId, videoId, videoDuration);
  const segmentEnd = segmentStart + segmentDuration;

  // Filter frames that fall within this segment's time range
  // Use inclusive start and exclusive end to avoid overlaps
  let segmentFrames = masterLayout
    .filter(frame => frame.time >= segmentStart && frame.time < segmentEnd)
    .slice(0, maxFrames);

  // If no frames found (very short segment), create a frame at segment midpoint
  if (segmentFrames.length === 0) {
    const midTime = segmentStart + segmentDuration / 2;
    // Use a simple frame number based on time in seconds (rounded)
    const frameNumber = Math.max(1, Math.round(midTime));
    segmentFrames = [{ frameNumber, time: midTime }];
  }

  // Add index to each frame
  return segmentFrames.map((frame, index) => ({ ...frame, index }));
};

/**
 * Clear video frame layout cache for a specific video
 */
export const clearVideoFrameLayout = (
  storyId: string,
  videoId: string,
): void => {
  const videoKey = `${storyId}-${videoId}`;
  videoFrameLayoutCache.delete(videoKey);
};

/**
 * Get cache statistics for debugging
 */
export const getFrameCacheStats = () => {
  const stats = new Map<string, number>();

  for (const key of frameUrlCache.keys()) {
    const videoKey = key.substring(0, key.lastIndexOf('-'));
    stats.set(videoKey, (stats.get(videoKey) || 0) + 1);
  }

  return {
    totalFrames: frameUrlCache.size,
    videoCount: stats.size,
    framesByVideo: Object.fromEntries(stats),
    layoutCacheSize: videoFrameLayoutCache.size,
  };
};
